import type { Dayjs } from 'dayjs';
import type { DateRange, PublicationDetailOut } from '@/api/generated/schemas';
import { inclusiveUpper } from './exclusivityWindow';

export type TimelineSource = 'pub' | 'ebook' | 'audio';

export interface TimelineEvent {
  date: string;
  labelKey: string;
  labelParams?: Record<string, number>;
  src: TimelineSource;
  end?: boolean;
  /** Set by hand rather than derived from the import — rendered to stand out. */
  highlight?: boolean;
}

const T = 'user_catalog.publications.show.timeline';

/**
 * Derives timeline events from publication and format availability
 * fields. Pure — the "today" marker is computed at render time.
 */
export const publicationEvents = (p: PublicationDetailOut): TimelineEvent[] => {
  const ev: TimelineEvent[] = [];
  if (p.first_available_from) {
    ev.push({
      date: p.first_available_from,
      labelKey: `${T}.first_available`,
      src: 'pub',
    });
  }
  p.available_ranges?.forEach((r, i) => {
    if (r.lower) {
      ev.push({
        date: r.lower,
        labelKey: `${T}.window_start`,
        labelParams: { n: i + 1 },
        src: 'pub',
      });
    }
    if (r.upper) {
      ev.push({
        date: r.upper,
        labelKey: `${T}.window_end`,
        labelParams: { n: i + 1 },
        src: 'pub',
        end: true,
      });
    }
  });
  // The earliest product early access; the backend already skips products
  // without a premiere.
  if (p.accessible_from) {
    ev.push({
      date: p.accessible_from,
      labelKey: `${T}.early_access`,
      src: 'pub',
      highlight: true,
    });
  }
  if (p.exclusive_ranges?.lower) {
    ev.push({
      date: p.exclusive_ranges.lower,
      labelKey: `${T}.exclusive_start`,
      src: 'pub',
    });
  }
  const exclusiveEnd = inclusiveUpper(p.exclusive_ranges?.upper);
  if (exclusiveEnd) {
    ev.push({
      date: exclusiveEnd.toISOString(),
      labelKey: `${T}.exclusive_end`,
      src: 'pub',
      end: true,
    });
  }
  for (const fmt of ['ebook', 'audio'] as const) {
    const f = p[fmt];
    if (!f) continue;
    if (f.published_at) {
      ev.push({
        date: f.published_at,
        labelKey: `${T}.${fmt}_published_at`,
        src: fmt,
      });
    }
    if (f.accessible_from) {
      ev.push({
        date: f.accessible_from,
        labelKey: `${T}.${fmt}_early_access`,
        src: fmt,
        highlight: true,
      });
    }
    if (f.first_available_from) {
      ev.push({
        date: f.first_available_from,
        labelKey: `${T}.${fmt}_first_available`,
        src: fmt,
      });
    }
    if (f.available_from) {
      ev.push({
        date: f.available_from,
        labelKey: `${T}.available_from`,
        src: fmt,
      });
    }
    if (f.available_till) {
      ev.push({
        date: f.available_till,
        labelKey: `${T}.${fmt}_available_till`,
        src: fmt,
        end: true,
      });
    }
    // The first non-exclusive day, like the window's upper bound — the event
    // marks the last exclusive day.
    const formatExclusiveEnd = inclusiveUpper(f.exclusive_until);
    if (formatExclusiveEnd) {
      ev.push({
        date: formatExclusiveEnd.toISOString(),
        labelKey: `${T}.${fmt}_exclusive_until`,
        src: fmt,
        end: true,
      });
    }
    if (f.withdrawal_at) {
      ev.push({
        date: f.withdrawal_at,
        labelKey: `${T}.${fmt}_withdrawal`,
        src: fmt,
        end: true,
      });
    }
  }
  return ev.sort((a, b) => a.date.localeCompare(b.date));
};

export type RangeState = 'active' | 'ended' | 'planned';

/**
 * State of an availability window, comparing inclusive calendar days.
 * `available_ranges` are half-open `[lower, upper)` like every backend
 * range, so a window still reads "active" on its upper-bound day. Left
 * as-is: availability is read-only and derived from format dates, and
 * no observation yet says which day operators expect. Exclusivity,
 * which operators do edit, uses `exclusivityState` instead.
 */
export const dateRangeState = (range: DateRange, now: Dayjs): RangeState => {
  if (range.lower && now.isBefore(range.lower, 'day')) return 'planned';
  if (range.upper && now.isAfter(range.upper, 'day')) return 'ended';
  return 'active';
};

/**
 * Index at which the "today" marker should be spliced into a
 * date-sorted event list. Events on the same calendar day come first.
 */
export const todayMarkerIndex = (
  events: TimelineEvent[],
  now: Dayjs
): number => {
  const idx = events.findIndex((e) => now.isBefore(e.date, 'day'));
  return idx === -1 ? events.length : idx;
};
